import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import {
  Stack,
  Button,
  InputLabel,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import * as yup from "yup";
import { Formik, Form } from "formik";
import { IconButton } from "@mui/material";
import { ReactComponent as CloseOutlined } from "../../assets/icons/close_icon.svg";
import { useDispatch } from "react-redux";
import { doUpdateUser, doGetUser } from "../../reducers/user/user.service";
import { useSelector } from "react-redux";
import { enqueueSnackbar } from "notistack";
import { doLogout } from "../../reducers/auth/auth.service";
import { useNavigate } from "react-router-dom";

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .matches(/^[a-z A-Z]+$/, "Please enter valid full name")
    .max(100, "Maximum 100 characters are allowed")
    .required("Name is required"),
});

const CreateAccountDialog = ({ open, editName = false, handleDialogClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currFullName = useSelector((state) => state.user.fullName);
  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{ name: currFullName }}
        validationSchema={validationSchema}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={async (
          values,
          { setFieldError, resetForm, isSubmitting, setSubmitting }
        ) => {
          setSubmitting(true);
          const trimmedValues = {};
          for (const key in values) {
            if (values.hasOwnProperty(key)) {
              if (typeof values[key] === "string") {
                trimmedValues[key] = values[key].trim();
              } else {
                trimmedValues[key] = values[key];
              }
            }
          }

          const resultAction = await dispatch(
            doUpdateUser({ fullName: trimmedValues.name })
          );
          if (doUpdateUser.fulfilled.match(resultAction)) {
            if (currFullName === null)
              enqueueSnackbar("Account created successfully", {
                variant: "success",
              });
            await dispatch(doGetUser());
            resetForm();
            handleDialogClose();
          } else if (resultAction.payload.status === 401) {
            await dispatch(doLogout());
            navigate("/");
          } else {
            resetForm();
            setFieldError("name", "Error updating name");
          }
          resetForm();
          setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          resetForm,
          isSubmitting,
        }) => (
          <Form>
            <Dialog
              scroll="body"
              open={open}
              onClose={() => {
                resetForm();
                handleDialogClose();
              }}
              disableScrollLock={false}
              fullWidth
              slotProps={{
                paper: {
                  component: "form",
                  sx: {
                    p: { xs: "20px !important", sm: "56px !important" },
                    borderRadius: {
                      xs: "20px !important",
                      sm: "50px !important",
                    },
                  },
                },
              }}
            >
              <DialogTitle
                variant="h3"
                sx={{
                  textAlign: "center",
                  p: 0,
                  mt: "15px",
                  fontWeight: 700,
                }}
              >
                {editName ? "UPDATE NAME" : "CREATE ACCOUNT"}
              </DialogTitle>

              <Typography
                sx={{
                  color: "white",
                  textAlign: "center",
                  fontFamily: "secondaryFontFamily",
                  fontSize: "14px",
                  fontWeight: 300,
                  mb: "50px",
                }}
              >
                {editName
                  ? "Enter your full name to update"
                  : "Setup your account and kick start your journey."}
              </Typography>

              <IconButton
                aria-label="close"
                onClick={() => {
                  resetForm();
                  handleDialogClose();
                }}
                sx={{
                  position: "absolute",
                  right: { sm: 20, xs: 10 },
                  top: { sm: 20, xs: 10 },
                  fontSize: "25px",
                  color: "#999999",
                }}
              >
                <CloseOutlined />
              </IconButton>
              <Stack direction={"column"} gap={{ md: 3, xs: 2 }}>
                <Box>
                  <InputLabel sx={{ color: "#1D1D3E", mb: "5px" }}>
                    Full name
                  </InputLabel>
                  <TextField
                    name="name"
                    placeholder="Enter full name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    sx={{ width: "100%" }}
                  ></TextField>
                </Box>
                <Button
                  disabled={isSubmitting}
                  variant="contained"
                  type="submit"
                  sx={{
                    mt: "50px",
                    mb: "10px",
                    width: "105px",
                    ml: "auto",
                    p: {
                      sm: "10px 15px 6px 10px !important",
                      xs: "6px 15px 2px 15px !important",
                    },
                  }}
                >
                  Continue
                </Button>
              </Stack>
            </Dialog>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default CreateAccountDialog;
